import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { Autocomplete } from '@components/moleculs/autocomplete';
import { LabeledField } from '@components/moleculs/labeled-field';
import { TextField } from '@components/moleculs/text-field';
import type { FormAutocompleteProps } from '@components/organisms/forms/form-autocomplete/types';

export const FormAutocomplete = <
    T,
    Multiple extends boolean | undefined = false,
    DisableClearable extends boolean | undefined = false,
    FreeSolo extends boolean | undefined = false,
>({
    name,
    isRequired,
    options,
    labelProps,
    className,
    textFieldProps,
    autocompleteProps,
    onChange,
    ...rest
}: FormAutocompleteProps<T, Multiple, DisableClearable, FreeSolo>) => {
    const { control } = useFormContext();

    const element = (
        <Controller
            name={name}
            control={control}
            render={({ field, fieldState }) => (
                <Autocomplete<T, Multiple, DisableClearable, FreeSolo>
                    {...field}
                    {...rest}
                    className={labelProps ? undefined : className}
                    renderInput={({ slotProps, ...params }) => (
                        <TextField
                            error={!!fieldState.error?.message}
                            helperText={fieldState.error?.message}
                            {...params}
                            {...textFieldProps}
                            InputProps={{
                                ...slotProps.input,
                                ...textFieldProps?.InputProps,
                            }}
                            inputProps={{
                                ...slotProps.htmlInput,
                                ...textFieldProps?.inputProps,
                            }}
                        />
                    )}
                    {...autocompleteProps}
                    options={options}
                    onChange={(_, newValue) => {
                        field.onChange(newValue);
                        if (onChange) {
                            onChange(newValue);
                        }
                    }}
                />
            )}
        />
    );

    if (labelProps) {
        return (
            <LabeledField
                {...labelProps}
                isRequired={isRequired || labelProps?.isRequired}
                className={className}
            >
                {element}
            </LabeledField>
        );
    }

    return element;
};
